import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  
  const reference = searchParams.get('reference');
  const trxref = searchParams.get('trxref');

  useEffect(() => {
    const verifyPayment = async () => {
      const paymentReference = reference || trxref;
      
      if (!paymentReference) {
        setStatus('error');
        setMessage('No payment reference found');
        return;
      }

      try {
        const response = await api.get(`/paystack/verify/${paymentReference}`);
        
        if (response.data.status === 'success') {
          setStatus('success');
          setMessage('Payment successful! Your subscription is now active.');
          toast.success('Payment verified successfully!');
        } else {
          setStatus('failed');
          setMessage(response.data.message || 'Payment verification failed');
          toast.error('Payment verification failed');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to verify payment');
        toast.error('Failed to verify payment');
      }
    };

    verifyPayment();
  }, [reference, trxref]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 sm:p-6">
      <div className="w-full max-w-md bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 sm:p-8 text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-4 animate-spin" />
            <h2 className="text-xl sm:text-2xl font-heading font-bold mb-2" data-testid="payment-verifying-status">
              Verifying Payment...
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Please wait while we confirm your payment with Paystack.
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-heading font-bold mb-2 text-green-500" data-testid="payment-success-status">
              Payment Successful!
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-6">{message}</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 h-10 sm:h-12"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </>
        )}
        
        {(status === 'failed' || status === 'error') && (
          <>
            <XCircle className="w-12 h-12 sm:w-16 sm:h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-heading font-bold mb-2 text-destructive" data-testid="payment-failed-status">
              {status === 'failed' ? 'Payment Failed' : 'Verification Error'}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/plans')}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full sm:w-auto"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
